import React, { useState, useMemo, useRef, useEffect } from 'react';
import Pagination from '../../components/Pagination.tsx';
// IMPORTANTE: Usar estilos estándar de Programas Académicos para consistencia visual
import styles from '../../styles/PantallaDeProgramasAcademicos.v2.module.css';
import { usePermissions } from '../../hooks/usePermissions';

/**
 * UserTable de Gestión de Usuarios
 * Rediseñado para seguir exactamente el patrón de ProgramsTable:
 * - Header con icono naranja y título
 * - Paginación en el header (no en el footer)
 * - Columnas con grid layout
 * - Badges de estado con colores estándar
 * - Botones de acción con iconos
 */

function UserTable({
  users,
  onView,
  onEdit,
  onDelete,
  serverPaging = false,
  page: serverPage = 1,
  totalPages: serverTotalPages = 1,
  onPageChange: onServerPageChange,
  totalCount = null,
}) {
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const totalPages = serverPaging ? Math.max(1, serverTotalPages) : Math.max(1, Math.ceil(users.length / pageSize));
  
  // Permisos del módulo Administración
  const { canEdit, canDelete } = usePermissions();
  const puedeEditar = canEdit('Administración');
  const puedeEliminar = canDelete('Administración');

  // Crear clave única basada en IDs para detectar cambios reales
  const usersKey = useMemo(() => {
    return (users || []).map(u => u.id).sort().join(',');
  }, [users]);

  const prevUsersKeyRef = useRef(usersKey);

  // Reiniciar página solo cuando realmente cambia la lista de usuarios
  useEffect(() => {
    if (!serverPaging && prevUsersKeyRef.current !== usersKey) {
      setPage(1);
      prevUsersKeyRef.current = usersKey;
    }
  }, [usersKey, serverPaging]);

  // Ajustar página si quedó fuera de rango
  useEffect(() => {
    if (!serverPaging && page > totalPages && totalPages > 0) {
      setPage(totalPages);
    }
  }, [page, totalPages, serverPaging]);

  const paginated = serverPaging ? users : users.slice((page - 1) * pageSize, page * pageSize);

  // Componente de botones de acción (patrón estándar con iconos de Builder.io)
  const ActionButtons = ({ user }) => (
    <div className={styles.actionButtons}>
      <button 
        aria-label="Ver detalles" 
        className={styles.actionButton} 
        onClick={() => onView(user.id)}
      >
        <img
          src="https://api.builder.io/api/v1/image/assets/189f954e013f4fa084ccafbef336b287/a1782ac5b190a9d29d8f798383eedda3f294d56b?placeholderIfAbsent=true"
          alt="Ver"
          className={styles.actionIcon}
        />
      </button>
      {puedeEditar && (
        <button 
          aria-label="Editar" 
          className={styles.actionButton} 
          onClick={() => onEdit(user.id)}
        >
          <img
            src="https://api.builder.io/api/v1/image/assets/189f954e013f4fa084ccafbef336b287/83eacec9f1f974b33615bbc95aea7ff7b2d13b0e?placeholderIfAbsent=true"
            alt="Editar"
            className={styles.actionIcon}
          />
        </button>
      )}
      {puedeEliminar && (
        <button 
          aria-label="Eliminar" 
          className={styles.actionButton} 
          onClick={() => onDelete(user.id, user.nombre)}
        >
          <img
            src="https://api.builder.io/api/v1/image/assets/189f954e013f4fa084ccafbef336b287/bef20ed4bd5293db48e37df8e511dca0fed7db2e?placeholderIfAbsent=true"
            alt="Eliminar"
            className={styles.actionIcon}
          />
        </button>
      )}
    </div>
  );

  // ESTRUCTURA REDISEÑADA: Siguiendo exactamente el patrón de ProgramsTable
  return (
    <div className={styles.tableContainer}>
      {/* HEADER: Título con icono naranja */}
      <div className={styles.tableHeader}>
        <div className={styles.tableTitle}>
          <div className={styles.tableTitleIcon}>
            <div className={styles.iconContainerOrange}>
              <span>👥</span>
            </div>
          </div>
          <h2 className={styles.tableTitleText}>Usuarios</h2>
        </div>
      </div>

      {/* HEADER ROW: Columnas de la tabla con fondo gris */}
      <div className={styles.tableHeaderRow} style={{ gridTemplateColumns: '1.5fr 1fr 1fr 1.2fr 1fr 0.8fr 1fr 0.8fr' }}>
        <span className={styles.columnHeader}>Nombre</span>
        <span className={styles.columnHeader}>Usuario</span>
        <span className={styles.columnHeader}>Rol</span>
        <span className={styles.columnHeader}>Institución</span>
        <span className={styles.columnHeader}>Campus</span>
        <span className={styles.columnHeader}>Estado</span>
        <span className={styles.columnHeader}>Último acceso</span>
        <span className={styles.columnHeaderWide}>Acciones</span>
      </div>

      {/* TABLE BODY: Filas de datos */}
      <div className={styles.tableBody}>
        {paginated.length === 0 && (
          <div className={styles.tableRow} style={{ gridTemplateColumns: '1fr' }}>
            <div className={styles.programInfo}>
              <span className={styles.programName}>No se encontraron usuarios.</span>
            </div>
          </div>
        )}
        {paginated.map((user) => (
          <div key={user.id} className={styles.tableRow} style={{ gridTemplateColumns: '1.5fr 1fr 1fr 1.2fr 1fr 0.8fr 1fr 0.8fr' }}>
            <span className={styles.cellData}>{user.nombre || '—'}</span>
            <span className={styles.cellData}>{user.usuario || '—'}</span>
            <span className={styles.cellData}>{user.rol || '—'}</span>
            <span className={styles.cellData}>{user.institucion || '—'}</span>
            <span className={styles.cellData}>{user.campus || '—'}</span>
            <div className={styles.statusCell}>
              <div className={user.estado === 'ACTIVO' ? styles.statusActive : styles.statusInactive}>
                <span>{user.estado || 'INACTIVO'}</span>
              </div>
            </div>
            <span className={styles.cellData}>{user.ultimo_acceso || '—'}</span>
            <ActionButtons user={user} />
          </div>
        ))}
      </div>

      {/* Paginación global reutilizada de Programas Académicos, centrada al pie */}
      {totalPages > 1 && (
        <Pagination
          page={serverPaging ? serverPage : page}
          totalPages={totalPages}
          onPageChange={serverPaging ? onServerPageChange : setPage}
        />
      )}
      {typeof totalCount === 'number' && (
        <div style={{ padding: '10px 16px 14px', borderTop: '1px solid #f0f0f0', color: '#6b7280', fontSize: 13 }}>
          Total de usuarios: {totalCount}
        </div>
      )}
    </div>
  );
}

export default UserTable;
